import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import ProductCard from '@/components/ProductCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Shop = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const sizes = ['PP', 'P', 'M', 'G', 'GG'];

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(`${API}/categories`);
        setCategories(response.data.categories);
      } catch (error) {
        console.error('Error fetching categories:', error);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (category) params.append('category', category);
        if (searchTerm) params.append('search', searchTerm);
        if (priceRange.min) params.append('min_price', priceRange.min);
        if (priceRange.max) params.append('max_price', priceRange.max);
        if (selectedSizes.length === 1) params.append('size', selectedSizes[0]);
        
        const response = await axios.get(`${API}/products?${params.toString()}`);
        let filteredProducts = response.data;
        
        // Client-side filtering for multiple sizes
        if (selectedSizes.length > 1) {
          filteredProducts = filteredProducts.filter(p => 
            selectedSizes.some(size => p.sizes.includes(size))
          );
        }
        
        setProducts(filteredProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setLoading(false);
      }
    };
    
    const debounce = setTimeout(fetchProducts, 300);
    return () => clearTimeout(debounce);
  }, [category, searchTerm, priceRange, selectedSizes]);

  const toggleSize = (size) => {
    setSelectedSizes(prev => 
      prev.includes(size) 
        ? prev.filter(s => s !== size)
        : [...prev, size]
    );
  };

  const clearFilters = () => {
    setSearchTerm('');
    setSelectedSizes([]);
    setPriceRange({ min: '', max: '' });
    setSearchParams({});
  };

  const getCategoryName = () => {
    if (!category) return 'Todos os Produtos';
    const cat = categories.find(c => c.id === category);
    return cat ? cat.name : 'Produtos';
  };

  const FiltersContent = () => (
    <div className="space-y-8">
      {/* Categories */}
      <div className="filter-section">
        <h4 className="filter-title">Categorias</h4>
        <div className="space-y-3">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={`/shop/${cat.id}`}
              className={`block text-sm transition-colors ${
                category === cat.id 
                  ? 'text-primary' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {cat.name}
            </a>
          ))}
        </div>
      </div>

      {/* Sizes */}
      <div className="filter-section">
        <h4 className="filter-title">Tamanhos</h4>
        <div className="flex flex-wrap gap-2">
          {sizes.map((size) => (
            <button
              key={size}
              onClick={() => toggleSize(size)}
              className={`size-btn ${selectedSizes.includes(size) ? 'selected' : ''}`}
              data-testid={`size-filter-${size}`}
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div className="filter-section">
        <h4 className="filter-title">Preço</h4>
        <div className="flex gap-3 items-center">
          <Input
            type="number"
            placeholder="Min"
            value={priceRange.min}
            onChange={(e) => setPriceRange(p => ({ ...p, min: e.target.value }))}
            className="input-luxury w-24 text-sm"
            data-testid="price-min"
          />
          <span className="text-muted-foreground">-</span>
          <Input
            type="number"
            placeholder="Max"
            value={priceRange.max}
            onChange={(e) => setPriceRange(p => ({ ...p, max: e.target.value }))}
            className="input-luxury w-24 text-sm"
            data-testid="price-max"
          />
        </div>
      </div>

      {/* Clear Filters */}
      {(selectedSizes.length > 0 || priceRange.min || priceRange.max || searchTerm) && (
        <Button 
          variant="ghost" 
          onClick={clearFilters}
          className="btn-ghost w-full"
          data-testid="clear-filters-btn"
        >
          Limpar Filtros
        </Button>
      )}
    </div>
  );

  return (
    <div className="min-h-screen pt-24" data-testid="shop-page">
      <div className="container-luxury py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-12">
          <div>
            <h1 className="font-serif text-4xl">{getCategoryName()}</h1>
            <p className="text-muted-foreground mt-2">
              {products.length} produto{products.length !== 1 ? 's' : ''} encontrado{products.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          <div className="flex gap-4 w-full md:w-auto">
            {/* Search */}
            <div className="relative flex-1 md:w-64">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar produtos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-luxury pl-6"
                data-testid="search-input"
              />
            </div>

            {/* Mobile Filters */}
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="outline" className="btn-outline px-4" data-testid="mobile-filters-btn">
                  <SlidersHorizontal className="w-4 h-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 bg-background border-white/10">
                <div className="mt-8">
                  <FiltersContent />
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>

        <div className="flex gap-12">
          {/* Desktop Sidebar */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FiltersContent />
          </aside>

          {/* Products Grid */}
          <div className="flex-1">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className="space-y-4">
                    <div className="aspect-[3/4] skeleton" />
                    <div className="h-4 w-3/4 skeleton" />
                    <div className="h-4 w-1/4 skeleton" />
                  </div>
                ))}
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <p className="text-2xl font-serif mb-4">Nenhum produto encontrado</p>
                <p className="text-muted-foreground mb-6">
                  Tente ajustar os filtros ou busque por outro termo
                </p>
                <Button onClick={clearFilters} className="btn-outline">
                  Limpar Filtros
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
